import type {
  ArrayConfigFieldData,
  ConfigService,
  ObjectConfigFieldData,
} from '@empathyco/playboard-core-domain'
import { computed, ref, watch } from 'vue'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { useInstance } from './use-instance'

export interface EndpointOption {
  context: string
  value: string
  label: string
  defaultBrowseField?: string
  defaultBrowseValue?: string
}

const endpoints = ref<EndpointOption[]>([])
const configuredDefaultEndpointId = ref<string>('')

const defaultEndpointId = computed<string>(() => configuredDefaultEndpointId.value)

let loadedInstanceId: string | undefined
let fetching = false

async function fetchEndpoints(instanceId: string): Promise<void> {
  if (loadedInstanceId === instanceId || fetching) {
    return
  }

  fetching = true
  try {
    const instanceManagementService = CONTAINER.get<ConfigService>(TYPES.ConfigService)
    const serviceConfigs = await instanceManagementService.read({
      instance: instanceId,
      service: 'playboard',
    })

    endpoints.value = (
      ((serviceConfigs.data as ObjectConfigFieldData).endpoints as ObjectConfigFieldData)
        .endpoints as ArrayConfigFieldData
    ).map((endpoint: any) => ({
      context: endpoint.context[0],
      value: endpoint.endpointId,
      label: endpoint.displayName,
      defaultBrowseField: endpoint.defaultBrowseField,
      defaultBrowseValue: endpoint.defaultBrowseValue,
    }))

    const configuredDefault = (
      ((serviceConfigs.data as ObjectConfigFieldData).endpoints as ObjectConfigFieldData)
        .defaultEndpointId as string
    )?.trim()

    configuredDefaultEndpointId.value =
      configuredDefault || endpoints.value.find(endpoint => endpoint.value)?.value || ''

    loadedInstanceId = instanceId
  } finally {
    fetching = false
  }
}

export function getDefaultEndpointId(): string {
  return defaultEndpointId.value
}

export function useEndpoints() {
  const { instanceConfig } = useInstance()

  watch(
    () => instanceConfig.value?.id,
    instanceId => {
      if (instanceId) {
        fetchEndpoints(instanceId)
      } else {
        endpoints.value = []
        configuredDefaultEndpointId.value = ''
        loadedInstanceId = undefined
      }
    },
    { immediate: true },
  )

  return {
    endpoints,
    defaultEndpointId,
  }
}
